import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, Loader2, ScanLine, Trash2, Clock, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api, formatDate, scoreBg, videoThumbUrl } from '../lib/api';
import { MOVE_TYPES, useLanguage } from '../i18n';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function Library() {
  const navigate = useNavigate();
  const { t, lang, moveLabel } = useLanguage();
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async (f) => {
    setItems(null);
    try {
      const qs = f && f !== 'all' ? `?move_type=${f}` : '';
      const { data } = await api.get(`/analyses${qs}`);
      setItems(data.items);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    load(filter);
  }, [filter]);

  const confirmDelete = async () => {
    const id = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/analyses/${id}`);
      setItems((prev) => prev.filter((i) => i.analysis_id !== id));
      toast.success(t('confirmDelete') + ' \u2713');
    } catch {
      toast.error(t('analysisFailedSub'));
    }
  };

  const chips = ['all', ...MOVE_TYPES.filter((m) => m !== 'other')];

  return (
    <div className="space-y-4" data-testid="library-page">
      <div className="pt-2">
        <h1 className="font-display font-bold text-2xl tracking-tight text-white">{t('navLibrary')}</h1>
        <p className="text-sm text-white/55 mt-1">{t('filterByMove')}</p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="library-filter-chips">
        {chips.map((m) => (
          <button
            key={m}
            onClick={() => setFilter(m)}
            data-testid={`library-filter-chip-${m}`}
            className={`shrink-0 px-3.5 py-1.5 rounded-[14px] text-xs font-medium border transition-colors ${
              filter === m
                ? 'bg-white/[0.12] border-white/25 text-white'
                : 'bg-white/[0.04] border-white/10 text-white/50 hover:bg-white/[0.07]'
            }`}
          >
            {m === 'all' ? t('all') : t('moves')[m]}
          </button>
        ))}
      </div>

      {!items ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full bg-white/[0.05] rounded-[16px]" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="ss-card p-10 text-center space-y-4" data-testid="library-empty-state">
          <span className="inline-flex w-14 h-14 rounded-[16px] bg-white/[0.07] border border-white/12 items-center justify-center">
            <FolderOpen size={24} className="text-white" />
          </span>
          <div>
            <h2 className="font-display font-bold text-white">{filter === 'all' ? t('emptyLibraryTitle') : t('emptyFilterTitle')}</h2>
            <p className="text-xs text-white/45 font-mono mt-1">{t('emptyLibrarySub')}</p>
          </div>
          <button onClick={() => navigate('/analyze')} data-testid="library-empty-analyze-cta" className="ss-btn-primary h-11 px-6 text-sm font-semibold inline-flex items-center gap-2">
            <ScanLine size={16} /> {t('analyzeNewSkill')}
          </button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map((item, idx) => (
            <motion.div
              key={item.analysis_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(idx * 0.04, 0.3) }}
              className="ss-card p-3 flex items-center gap-3 cursor-pointer hover:border-white/25 transition-colors group"
              onClick={() => navigate(`/result/${item.analysis_id}`)}
              data-testid={`library-item-${item.analysis_id}`}
            >
              <div className="w-20 h-14 rounded-[12px] overflow-hidden bg-black/60 border border-white/10 shrink-0 flex items-center justify-center relative">
                {item.has_thumbnail ? (
                  <img src={videoThumbUrl(item.analysis_id)} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <ScanLine size={16} className="text-white/30" />
                )}
                {item.duration && (
                  <span className="absolute bottom-1 right-1 bg-black/75 rounded-[8px] px-1 py-px font-mono text-[9px] text-white/80 flex items-center gap-0.5">
                    <Clock size={8} /> {item.duration.toFixed(0)}s
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate text-white/90 flex items-center gap-1.5">
                  {item.status === 'done' ? moveLabel(item.move_type, item) : item.status === 'failed' ? t('failed') : t('processing')}
                  {item.post_id && <Flame size={11} className="text-white/50 shrink-0" title={t('sharedOnPitch')} />}
                </div>
                <div className="font-mono text-[11px] text-white/40">{formatDate(item.created_at, lang)}</div>
              </div>
              <div className="flex items-center gap-2">
                {item.status === 'analyzing' ? (
                  <Loader2 size={16} className="animate-spin text-white/50" />
                ) : item.overall_score != null ? (
                  <span className={`border rounded-[12px] px-2.5 py-1 font-mono text-xs font-medium tabular-nums ${scoreBg(item.overall_score)}`}>
                    {item.overall_score}
                  </span>
                ) : item.status === 'failed' ? (
                  <span className="border rounded-[12px] px-2.5 py-1 font-mono text-[10px] bg-[rgba(231,111,123,0.1)] text-[#E76F7B] border-[rgba(231,111,123,0.3)]">!</span>
                ) : null}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(item.analysis_id);
                  }}
                  data-testid={`library-delete-${item.analysis_id}`}
                  className="w-8 h-8 rounded-[12px] flex items-center justify-center text-white/30 hover:text-[#E76F7B] hover:bg-[rgba(231,111,123,0.08)] transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#1E1E1E] border-white/10 rounded-[16px] max-w-sm" data-testid="library-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-white">{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50 text-sm">{t('deleteConfirmDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/[0.06] border-white/10 rounded-[14px]" data-testid="library-delete-cancel">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="library-delete-confirm"
              className="bg-[rgba(231,111,123,0.14)] text-[#E76F7B] border border-[rgba(231,111,123,0.3)] rounded-[14px] hover:bg-[rgba(231,111,123,0.2)]"
            >
              {t('confirmDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
